/**
 * Resolve a live EIP-1193 provider for wallet operations.
 *
 * Auth can show a logged-in address from persisted tokens while WalletKit's
 * in-memory `provider` is empty after refresh. Signature flows must use an
 * existing kit provider or the browser-injected `window.ethereum` — never open
 * WalletKit's Connect Wallet modal. Only login may call `walletKit.connect()`.
 */

import { getWalletKit } from '@web3jskit/walletkit';
import { switchOrAddChain, type AddChainOverrides } from './evmChains';
import { WalletError, probeWalletAlive, type ProviderLike } from './walletError';

/** Poll interval after `connect()` while WalletKit mounts the EIP-1193 provider. */
export const PROVIDER_POLL_INTERVAL_MS = 50;
/** Max poll attempts (~2s) before failing with `noProvider`. */
export const PROVIDER_POLL_MAX_ATTEMPTS = 40;

export type Eip1193Provider = ProviderLike;

function isProviderLike(value: unknown): value is Eip1193Provider {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as Eip1193Provider).request === 'function'
  );
}

/**
 * Browser-injected EIP-1193 provider (MetaMask / OKX / etc.).
 */
export function getInjectedEip1193Provider(): Eip1193Provider | null {
  if (typeof window === 'undefined') return null;
  const ethereum = (window as Window & { ethereum?: unknown }).ethereum;
  return isProviderLike(ethereum) ? ethereum : null;
}

/**
 * Wait until `getWalletKit().provider` is mounted after `connect()`.
 *
 * @throws {WalletError} `noProvider` when the provider never appears
 */
export async function waitForEip1193ProviderAfterConnect(
  pollIntervalMs: number = PROVIDER_POLL_INTERVAL_MS,
  maxAttempts: number = PROVIDER_POLL_MAX_ATTEMPTS
): Promise<Eip1193Provider> {
  for (let i = 0; i < maxAttempts; i += 1) {
    const p = getWalletKit().provider as Eip1193Provider | null | undefined;
    if (isProviderLike(p)) {
      return p;
    }
    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }
  throw new WalletError('noProvider');
}

/**
 * Resolve a live provider WITHOUT opening WalletKit Connect UI.
 *
 * Used by signature flows after the user is already authenticated.
 * Candidates: WalletKit.provider, then window.ethereum.
 * Does not require `eth_accounts` to be non-empty (locked wallets still probe).
 *
 * @throws {WalletError} `noProvider` when no responsive provider exists
 */
export async function resolveLiveEip1193Provider(): Promise<Eip1193Provider> {
  const kitProvider = getWalletKit().provider as Eip1193Provider | null | undefined;
  const candidates: Eip1193Provider[] = [];
  if (isProviderLike(kitProvider)) {
    candidates.push(kitProvider);
  }
  const injected = getInjectedEip1193Provider();
  if (injected && injected !== kitProvider) {
    candidates.push(injected);
  }

  let lastError: unknown;
  for (const candidate of candidates) {
    try {
      await probeWalletAlive(candidate);
      return candidate;
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError instanceof WalletError) throw lastError;
  throw new WalletError('noProvider');
}

/**
 * Ensure a live provider for login (may open WalletKit Connect UI).
 *
 * 1. Reuse an existing live WalletKit provider.
 * 2. Otherwise, `walletKit.connect()` + wait for mount + probe.
 *
 * Login intentionally does not fall back to an arbitrary `window.ethereum`.
 * In multi-wallet browsers that would bypass WalletKit's wallet picker and may
 * route the request to a different extension than the user intended.
 *
 * @throws {WalletError} classified wallet errors from probe / connect
 */
export async function ensureLiveEip1193Provider(): Promise<Eip1193Provider> {
  const walletKit = getWalletKit();
  const kitProvider = walletKit.provider as Eip1193Provider | null | undefined;
  if (isProviderLike(kitProvider)) {
    try {
      await probeWalletAlive(kitProvider);
      return kitProvider;
    } catch {
      // Fall through to WalletKit connect UI for a fresh provider.
    }
  }

  await walletKit.connect();
  const provider = await waitForEip1193ProviderAfterConnect();
  await probeWalletAlive(provider);
  return provider;
}

/**
 * Drop the current WalletKit session so the next login can open the
 * wallet picker instead of reusing the last connected account.
 */
export async function disconnectWalletKitSession(): Promise<void> {
  await getWalletKit().disconnect();
}

/**
 * Switch the connected wallet to `chainId`.
 *
 * Prefers `walletKit.switchNetwork`: it dispatches to the active connector, so
 * hosted bridges (GelWallet) and injected extensions each get the call shape
 * they actually implement. Falls back to a raw EIP-1193
 * `wallet_switchEthereumChain` / `wallet_addEthereumChain` pair for kits that
 * do not expose it.
 */
export async function switchNetworkViaWalletKit(
  provider: Eip1193Provider,
  chainId: number,
  overrides: AddChainOverrides = {}
): Promise<void> {
  const { switchNetwork } = getWalletKit();
  if (typeof switchNetwork === 'function') {
    await switchNetwork(chainId);
    return;
  }
  await switchOrAddChain(provider, chainId, overrides);
}
